import React, { useRef, useState } from 'react';
import { Plus, Trash2, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { LIGHT_TYPES, AIMED, MAX_LIGHTS, newLight } from '../lib/lights';

const PRESETS = [['warm', 'WARM'], ['balanced', 'BALANCED'], ['neutral', 'NEUTRAL']];
const TYPE_LABEL = Object.fromEntries(LIGHT_TYPES);

export default function LightRig({ lights, preset, onChange, onPreset, onOpen }) {
  const [openId, setOpenIdRaw] = useState(lights[0]?.id || null);
  const setOpenId = (id) => { setOpenIdRaw(id); onOpen?.(id); };
  const update = (id, patch) => onChange(lights.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const add = () => { const l = newLight(); onChange([...lights, l]); setOpenId(l.id); };
  const remove = (id) => onChange(lights.filter((l) => l.id !== id));

  return (
    <section className="space-y-2" data-testid="light-rig">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-osd text-[11px] text-[#8b6a3e] tracking-widest">LIGHTS · {lights.length}</div>
        <div className="flex gap-1.5 items-center">
          <span className="font-osd text-[10px] text-[#8b6a3e] tracking-widest">START FROM</span>
          {PRESETS.map(([k, t]) => (
            <button key={k} type="button" onClick={() => onPreset(k)} data-testid={`light-preset-${k}`}
                    className={`header-tab h-8 px-2 font-osd text-[10px] tracking-wider border-2 rounded-sm ${preset === k ? 'header-tab-active' : ''}`}>{t}</button>
          ))}
        </div>
      </div>
      {lights.length === 0 && <div className="font-osd text-[11px] text-[#ff8a5c]" data-testid="light-rig-empty">NO LIGHTS · THE MODEL WILL LOOK DARK UNLESS IT'S UNLIT OR MATCAP</div>}
      <ul className="space-y-2">
        {lights.map((l, i) => (
          <LightCard key={l.id} l={l} index={i} open={openId === l.id} onToggle={() => setOpenId(openId === l.id ? null : l.id)}
                     onChange={(p) => update(l.id, p)} onRemove={() => remove(l.id)} />
        ))}
      </ul>
      <button type="button" onClick={add} disabled={lights.length >= MAX_LIGHTS} data-testid="light-add"
              className="admin-action w-full min-h-[44px] flex items-center justify-center gap-2 border-2 border-dashed border-[#5a3418] text-[#ffc968] rounded font-osd text-xs tracking-widest hover:border-[#f5a623] disabled:opacity-40">
        <Plus className="w-4 h-4" /> ADD LIGHT {lights.length >= MAX_LIGHTS ? `(MAX ${MAX_LIGHTS})` : ''}
      </button>
    </section>
  );
}

function LightCard({ l, index, open, onToggle, onChange, onRemove }) {
  const aimed = AIMED.has(l.type);
  return (
    <li className={`border-2 rounded-lg bg-[#0f0704] ${open ? 'border-[#5a3418]' : 'border-[#3a2512]'}`} data-testid={`light-${index}`}>
      <div className="flex items-center gap-2 p-2">
        <button type="button" onClick={onToggle} aria-expanded={open} className="flex-1 min-w-0 flex items-center gap-2 min-h-[36px] text-left" data-testid={`light-${index}-toggle`}>
          <ChevronDown className={`w-4 h-4 text-[#f5a623] transition-transform ${open ? '' : '-rotate-90'}`} />
          <span className="w-4 h-4 rounded-full border border-[#1a0e05] shrink-0" style={{ background: l.color }} />
          <span className={`font-osd text-xs tracking-wider truncate ${l.on === false ? 'text-[#8b6a3e] line-through' : 'text-[#ffe9b8]'}`}>
            {String(index + 1).padStart(2, '0')} · {TYPE_LABEL[l.type]} · {l.intensity.toFixed(2)}{aimed ? ` · ${Math.round(l.az)}° / ${Math.round(l.el)}°` : ''}
          </span>
        </button>
        <IconBtn onClick={() => onChange({ on: l.on === false })} label={l.on === false ? 'Turn light on' : 'Turn light off'} testId={`light-${index}-onoff`}>
          {l.on === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </IconBtn>
        <IconBtn onClick={onRemove} label="Delete light" testId={`light-${index}-delete`} danger><Trash2 className="w-4 h-4" /></IconBtn>
      </div>
      {open && <LightFields l={l} index={index} aimed={aimed} onChange={onChange} />}
    </li>
  );
}

function LightFields({ l, index, aimed, onChange }) {
  return (
    <div className="px-3 pb-3 space-y-3 border-t border-[#2a1608] pt-3">
      <div className="flex gap-1 flex-wrap" role="radiogroup" aria-label="Light type">
        {LIGHT_TYPES.map(([k, t]) => (
          <button key={k} type="button" role="radio" aria-checked={l.type === k} onClick={() => onChange({ type: k })} data-testid={`light-${index}-type-${k}`}
                  className={`header-tab h-8 px-2 font-osd text-[10px] tracking-wider border-2 rounded-sm ${l.type === k ? 'header-tab-active' : ''}`}>{t}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-4">
        <ColorField label={l.type === 'hemisphere' ? 'SKY' : 'COLOUR'} value={l.color} onChange={(v) => onChange({ color: v })} testId={`light-${index}-color`} />
        {l.type === 'hemisphere' && <ColorField label="GROUND" value={l.ground} onChange={(v) => onChange({ ground: v })} testId={`light-${index}-ground`} />}
      </div>
      <Range label="INTENSITY" min={0} max={20} step={0.05} value={l.intensity} onChange={(v) => onChange({ intensity: v })} testId={`light-${index}-intensity`} />
      {aimed && (
        <div className="flex gap-4 items-start flex-wrap">
          <DirectionBall az={l.az} el={l.el} onChange={onChange} testId={`light-${index}-ball`} />
          <div className="flex-1 min-w-[160px] space-y-2">
            <Range label="LEFT / RIGHT" min={-180} max={180} step={1} value={l.az} onChange={(v) => onChange({ az: v })} unit="°" testId={`light-${index}-az`} />
            <Range label="DOWN / UP" min={-90} max={90} step={1} value={l.el} onChange={(v) => onChange({ el: v })} unit="°" testId={`light-${index}-el`} />
            {l.type !== 'directional' && <Range label="DISTANCE" min={0.2} max={10} step={0.1} value={l.dist} onChange={(v) => onChange({ dist: v })} unit="×" testId={`light-${index}-dist`} />}
            {l.type === 'spot' && <Range label="CONE" min={1} max={90} step={1} value={l.angle} onChange={(v) => onChange({ angle: v })} unit="°" testId={`light-${index}-angle`} />}
            {l.type === 'spot' && <Range label="SOFT EDGE" min={0} max={1} step={0.05} value={l.penumbra} onChange={(v) => onChange({ penumbra: v })} testId={`light-${index}-penumbra`} />}
          </div>
        </div>
      )}
    </div>
  );
}

// Front view of a ball: the dot shows where the light sits. Solid = in front of the model, hollow = behind.
function DirectionBall({ az, el, onChange, testId }) {
  const ref = useRef(null);
  const behind = Math.abs(az) > 90;
  const a = (az * Math.PI) / 180;
  const e = (el * Math.PI) / 180;
  const x = Math.sin(a) * Math.cos(e);
  const y = Math.sin(e);
  const R = 56;
  const setFrom = (ev) => {
    const r = ref.current.getBoundingClientRect();
    let px = (ev.clientX - r.left - r.width / 2) / R;
    let py = -(ev.clientY - r.top - r.height / 2) / R;
    const len = Math.hypot(px, py);
    if (len > 1) { px /= len; py /= len; }
    const pz = Math.sqrt(Math.max(0, 1 - px * px - py * py)) * (behind ? -1 : 1);
    onChange({ az: Math.round((Math.atan2(px, pz) * 180) / Math.PI), el: Math.round((Math.asin(Math.max(-1, Math.min(1, py))) * 180) / Math.PI) });
  };
  const down = (ev) => { ev.currentTarget.setPointerCapture(ev.pointerId); setFrom(ev); };
  const move = (ev) => { if (ev.buttons) setFrom(ev); };
  const flip = () => onChange({ az: az >= 0 ? 180 - az : -180 - az });
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div ref={ref} onPointerDown={down} onPointerMove={move} role="slider" aria-label="Light direction" aria-valuetext={`${Math.round(az)}°, ${Math.round(el)}°`}
           className="relative rounded-full cursor-crosshair touch-none select-none"
           style={{ width: R * 2, height: R * 2, background: 'radial-gradient(circle at 40% 35%, #4a3420, #1a0e05 70%)', border: '2px solid #5a3418' }}
           data-testid={testId}>
        <div className="absolute inset-0 rounded-full pointer-events-none" style={{ boxShadow: 'inset 0 0 0 1px rgba(245,166,35,0.15)' }} />
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#f5a623]/15" />
        <div className="absolute top-1/2 left-0 right-0 h-px bg-[#f5a623]/15" />
        <div className="absolute w-4 h-4 -ml-2 -mt-2 rounded-full pointer-events-none"
             style={{ left: R + x * R, top: R - y * R, background: behind ? 'transparent' : '#ffc968', border: '2px solid #ffc968', boxShadow: '0 0 10px #f5a623' }} />
      </div>
      <button type="button" onClick={flip} className="font-osd text-[10px] tracking-widest text-[#ffc968] border border-[#5a3418] rounded px-2 py-1 hover:border-[#f5a623]" data-testid={`${testId}-flip`}>
        {behind ? 'BEHIND · MOVE TO FRONT' : 'FRONT · MOVE BEHIND'}
      </button>
    </div>
  );
}

function NumInput({ min, max, step, value, onChange, testId, label }) {
  const [text, setText] = useState(null);
  const commit = () => {
    if (text !== null && text !== '' && !Number.isNaN(Number(text))) onChange(Math.min(max, Math.max(min, Number(text))));
    setText(null);
  };
  return (
    <input type="number" min={min} max={max} step={step} value={text ?? Number(value.toFixed(2))}
           onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && commit()}
           className="w-16 bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] text-right px-1 rounded" data-testid={testId} aria-label={label} />
  );
}

function Range({ label, min, max, step, value, onChange, unit = '', testId }) {
  return (
    <label className="block space-y-1">
      <div className="flex justify-between font-osd text-[10px] tracking-widest">
        <span className="text-[#8b6a3e]">{label}</span>
        <NumInput min={min} max={max} step={step} value={value} onChange={onChange} testId={`${testId}-input`} label={`${label} value${unit ? ` (${unit})` : ''}`} />
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
             className="w-full h-6 accent-[#f5a623] cursor-pointer" data-testid={testId} />
    </label>
  );
}

function ColorField({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center gap-2">
      <span className="font-osd text-[10px] text-[#8b6a3e] tracking-widest">{label}</span>
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-8 bg-transparent border-2 border-[#3a2512] rounded cursor-pointer" data-testid={testId} />
      <span className="font-osd text-[10px] text-[#ffe9b8] uppercase">{value}</span>
    </label>
  );
}

function IconBtn({ children, onClick, label, testId, danger }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testId}
            className={`w-9 h-9 flex items-center justify-center border-2 rounded ${danger ? 'border-[#5a2414] text-[#ff8a5c] hover:border-[#ff6a3a]' : 'border-[#3a2512] text-[#ffc968] hover:border-[#f5a623]'}`}>
      {children}
    </button>
  );
}
